'use client'

import type { ReactNode } from 'react'
import { EditorSurfaceProvider, type EditorSkin, type EditorSurfaceValue } from '@/deskUiSkeletons/editor/EditorSurfaceContext'
import { editorAttachmentsUiSkin, editorModalSkin, modernSettingsSkin } from '@/msgComponents/skins/editorModalSkin'
import { FORUM_RICH_EDIT_VARIANT, RICH_EDIT_POLICY, forumRichEditSkin } from './richEdit.config'

const forumEditorSkin: EditorSkin = {
  headShort: '!hidden',
  head: {
    root: 'flex flex-col gap-2 border-b border-[#006699] bg-[#eef3f8] px-3 py-2',
    nameKeyRow: 'flex min-w-0 items-center gap-2',
    fromLabel: '',
    recipientsRow: 'flex min-w-0 items-center gap-2',
    subjectRow: 'flex min-w-0 items-center gap-2',
    subjectLabel: 'flex-none text-[10px] font-bold text-[#006699]',
    subjectInputBox: 'min-w-0 flex-1 border border-[#006699] bg-white',
    subjectInput: '!m-0 block !w-full box-border !rounded-none !border-0 !bg-white !p-1.5 text-[11px] text-black shadow-none outline-none',
    compatControl: 'hidden pointer-events-none',
  },
  nameKey: {
    block: 'flex w-full flex-col gap-1',
    moreButton: '',
    advancedOpen: 'block',
    advancedClosed: 'hidden',
  },
  smartName: {
    root: 'flex w-full min-w-0 items-center gap-1 text-[10px]',
    sign: 'box-border h-7 flex-none border border-[#006699] bg-white px-2 pt-1.5 text-[#006699]',
    signStatus: { busy: 'text-[#555]', ok: 'text-green-700', wrong: 'text-red-700' },
    inputBox: 'relative flex h-7 min-w-[235px] flex-1 items-center overflow-hidden border border-[#006699] bg-white',
    inputBoxWrong: 'border-red-700',
    input: '!m-0 h-6 !w-full !rounded-none !border-0 !bg-white !p-1 text-[11px] text-black shadow-none outline-none',
    actions: 'group relative box-border h-7 flex-none py-1 pl-0.5 pr-1',
    check: 'block cursor-pointer px-1 py-0.5 font-bold text-[#006699] hover:underline',
    quick: 'absolute left-0 top-full z-[100005] hidden group-hover:block group-focus-within:block',
    quickItems: 'w-[150px] border border-[#006699] bg-white p-1 text-[10px]',
    quickItem: 'mb-0.5 inline-block cursor-pointer bg-[#eef3f8] p-1 text-black',
  },
  extName: {
    root: 'flex flex-col gap-1 text-[10px]',
    quickRow: 'flex flex-wrap items-center gap-1',
    label: 'p-1 font-bold text-[#006699]',
    quickBtn: 'cursor-pointer border border-[#006699] bg-[#eef3f8] p-1 text-[10px] text-black hover:underline',
    subKeyBtn: 'cursor-pointer bg-[#006699] p-1 text-[10px] text-white hover:underline',
    box: 'relative max-w-[80%] overflow-hidden border border-[#006699] bg-white pr-8',
    textarea: '!m-0 block box-border min-h-[28px] !w-full resize-none !rounded-none !border-0 !bg-white !p-1.5 text-[11px] text-black outline-none',
    mirror: 'hidden',
    checkRow: 'flex flex-wrap items-center gap-1',
    subKeyText: 'block bg-[#006699] p-1 text-[10px] text-white empty:hidden',
    check: 'cursor-pointer p-1 font-bold text-[#006699] hover:underline',
    emailInfo: 'p-1 text-[9px] text-[#555]',
    emailEdit: 'cursor-pointer p-1 text-[9px] text-[#006699] underline',
    code: 'hidden flex-wrap items-center gap-2 text-[10px]',
    codeInput: 'w-[250px] text-right text-[10px]',
    codeBtn: 'cursor-pointer p-1 text-[#006699]',
    codeInfo: 'text-[9px] text-[#555]',
  },
  recipients: {
    root: 'flex w-full items-center gap-2 text-[10px]',
    label: 'flex-none font-bold text-[#006699]',
    field: 'flex min-w-0 flex-1 flex-wrap items-center gap-1 overflow-hidden border border-[#006699] bg-white pr-8',
    inputWrap: 'min-w-[60px] flex-1 overflow-hidden',
    input: '!mt-0 block !w-full !rounded-none !border-0 !bg-transparent !p-1.5 text-[11px] text-black shadow-none outline-none',
    inputErr: '!text-[#b91c1c] placeholder:text-[#b91c1c]',
    inputErrHint: 'flex-none self-center whitespace-nowrap pr-2 text-[10px] text-[#b91c1c]',
    list: 'my-1 ml-1 flex flex-wrap items-center gap-1',
    token: 'flex h-5 cursor-pointer items-center border border-[#006699] px-2 leading-5 text-black',
    tokenKind: { email: 'bg-[#dfe6ef]', msg: 'bg-[#ffffcc]', self: 'bg-[#006699]' },
    tokenText: 'max-h-5 text-[10px] font-bold text-[#006699]',
    tokenTextSelf: 'text-white',
    tokenX: 'ml-1 cursor-pointer font-bold text-[#555] hover:text-red-700',
    tokenLocked: '!cursor-default',
    tokenXSelf: 'text-[#dfe6ef]',
    compat: 'hidden',
  },
  settings: modernSettingsSkin,
  body: {
    root: 'flex min-h-0 flex-1 flex-col bg-white',
    tools: 'flex min-h-8 flex-wrap items-center gap-1 border-b border-[#006699] bg-[#dfe6ef] px-2 py-1',
    toolButton: 'cursor-pointer border border-[#006699] bg-[#eef3f8] px-2 py-1 text-[10px] font-bold text-[#006699]',
    toolButtonActive: 'cursor-pointer border border-[#003f63] bg-[#006699] px-2 py-1 text-[10px] font-bold text-white',
    legacyToolbarHidden: '',
    richFoldWrap: '',
    richFoldButton: '',
    textWrap: 'min-h-0 flex-1',
    textInner: 'h-full min-h-[420px]',
    textarea: 'box-border h-full min-h-[420px] w-full resize-none border-0 bg-white p-3 text-[12px] leading-5 text-black outline-none',
    richSurface: 'box-border h-full min-h-[420px] w-full overflow-auto border-0 bg-white p-3 text-[12px] leading-5 text-black outline-none',
    richToolbar: 'flex min-w-0 flex-wrap items-center gap-1',
    richButton: 'h-6 min-w-6 cursor-pointer border border-[#006699] bg-white px-1.5 text-[10px] leading-none text-[#006699] hover:bg-[#dfe6ef]',
  },
  modal: editorModalSkin,
  attachmentsUi: editorAttachmentsUiSkin,
}

const forumEditorSurface: EditorSurfaceValue = {
  variant: 'std',
  skin: forumEditorSkin,
  creationProductPath: 'forum',
  registeredUsersRule: 'forced-hidden',
  richEditVariant: FORUM_RICH_EDIT_VARIANT,
  richEditPolicy: RICH_EDIT_POLICY,
  richEditSkin: forumRichEditSkin,
  richEditDefaultFormat: 'html',
}

export function ForumEditorSurfaceProvider({ children }: { children: ReactNode }) {
  return <EditorSurfaceProvider value={forumEditorSurface}>{children}</EditorSurfaceProvider>
}
